/**
 * Streaming fetch utility for SSE (Server-Sent Events) responses
 * Handles real-time streaming of chatbot responses from the backend
 */

/**
 * Creates an AbortController with optional timeout
 * @param {number} timeoutMs - Timeout in milliseconds (default: 60000)
 * @returns {{ controller: AbortController, clear: () => void }}
 */
export const createStreamController = (timeoutMs = 60000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return {
    controller,
    clear: () => clearTimeout(timeoutId),
  };
};

/**
 * Makes a streaming POST request using fetch API
 * Parses SSE format: data: {"content": "text", "done": false}
 *
 * @param {Object} options
 * @param {string} options.url - The endpoint URL
 * @param {Object|FormData} options.body - Request body (will be JSON stringified if object)
 * @param {AbortController} [options.controller] - Optional AbortController for cancellation
 * @param {(accumulatedText: string) => void} options.onChunk - Called with accumulated text on each chunk
 * @param {(finalText: string) => void} options.onComplete - Called when streaming is complete
 * @param {(error: Error) => void} options.onError - Called on error
 * @param {Object} [options.headers] - Additional headers
 * @returns {Promise<void>}
 */
export const streamingFetch = async ({
  url,
  body,
  controller,
  onChunk,
  onComplete,
  onError,
  headers = {},
}) => {
  let fullText = "";

  try {
    const isFormData = body instanceof FormData;

    const fetchHeaders = {
      Accept: "text/event-stream",
      ...headers,
    };

    // Only set Content-Type for JSON bodies, let browser handle FormData
    if (!isFormData) {
      fetchHeaders["Content-Type"] = "application/json";
    }

    const response = await fetch(url, {
      method: "POST",
      headers: fetchHeaders,
      body: isFormData ? body : JSON.stringify(body),
      signal: controller?.signal,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    // Check if response is actually a stream
    const contentType = response.headers.get("content-type");

    // Handle non-streaming JSON response (fallback)
    if (contentType?.includes("application/json")) {
      const jsonData = await response.json();
      const text = jsonData.response || jsonData.content || "";
      onChunk?.(text);
      onComplete?.(text);
      return;
    }

    // Handle streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        // Process any remaining buffer
        if (buffer.trim()) {
          processSSELines(buffer, (text) => {
            fullText += text;
          });
        }
        onComplete?.(fullText);
        break;
      }

      // Decode the chunk and add to buffer
      buffer += decoder.decode(value, { stream: true });

      // Process complete lines from buffer
      const lines = buffer.split("\n");
      // Keep the last potentially incomplete line in buffer
      buffer = lines.pop() || "";

      for (const line of lines) {
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;

        // Parse SSE data line
        if (trimmedLine.startsWith("data:")) {
          const jsonStr = trimmedLine.slice(5).trim(); // Remove "data:" prefix

          if (!jsonStr) continue;

          try {
            const parsed = JSON.parse(jsonStr);

            if (parsed.content) {
              fullText += parsed.content;
              onChunk?.(fullText);
            }

            // Check if streaming is complete
            if (parsed.done === true) {
              onComplete?.(fullText);
              reader.cancel();
              return;
            }
          } catch (parseError) {
            // If JSON parsing fails, treat as plain text chunk
            console.warn("Failed to parse SSE JSON:", jsonStr, parseError);
          }
        }
      }
    }
  } catch (error) {
    // Don't report abort errors as they're intentional
    if (error.name === "AbortError") {
      console.log("Streaming request was aborted");
      return;
    }

    console.error("Streaming fetch error:", error);
    onError?.(error);
  }
};

/**
 * Helper to process SSE lines from a buffer
 * @param {string} buffer
 * @param {(content: string) => void} onContent
 */
const processSSELines = (buffer, onContent) => {
  const lines = buffer.split("\n");

  for (const line of lines) {
    const trimmedLine = line.trim();
    if (!trimmedLine || !trimmedLine.startsWith("data:")) continue;

    const jsonStr = trimmedLine.slice(5).trim();
    if (!jsonStr) continue;

    try {
      const parsed = JSON.parse(jsonStr);
      if (parsed.content) {
        onContent(parsed.content);
      }
    } catch {
      // Ignore parse errors for incomplete data
    }
  }
};

export default streamingFetch;
